import React, { useEffect, useState } from "react";
import { useMap } from "react-leaflet";
import { Box, FormControl, FormLabel, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import DescriptionIcon from "@mui/icons-material/Description";
import EventIcon from "@mui/icons-material/Event";
import PlaceIcon from "@mui/icons-material/Place";
import ImportContactsIcon from "@mui/icons-material/ImportContacts";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import NoteAltIcon from "@mui/icons-material/NoteAlt";

import { NYHalfWidthNumberToFillWidthNumber } from "@/utils/utils";
import {
  Button,
  ButtonVariantOption,
  ButtonColorOption,
} from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { MemoModal } from "./MemoModal";
import { OtherButtonModal } from "./OtherButtonModal";
import { formatArea, formatDate } from "../utils";
import { getGoogleMapsUrl } from "../utils/openGoogleMaps";
import { getChikaMapUrl } from "../utils/openChikaMap";
import { useFeatureFlags } from "@/configs/featureFlag";
import { addReceptionBookTagUpdateListener } from "../utils/tagSync/receptionBookTagSync";
import { addReceptionBookRemarksUpdateListener } from "../utils/tagSync/receptionBookRemarksSync";
import {
  addReceptionBookAdditionalFieldsUpdateListener,
  dispatchReceptionBookAdditionalFieldsUpdate,
} from "../utils/tagSync/receptionBookAdditionalFieldsSync";
import type { IPopupBookContent } from "../types/popup";
import type { ChibanInput } from "@/types/kaokuNumber";

/**
 * 登記取得画面へのURLを生成する
 * @param params.prefName 都道府県名
 * @param params.cityName 市区町村名
 * @param params.address 所在（市区町村名を含む場合は自動で取り除く）
 * @param params.chiban 地番
 * @param params.realEstate 不動産種別
 * @return 登記取得画面のURLを返す（パラメータが不足している場合は/acquirebookのみ）
 */
const buildAcquireBookUrl = (params: {
  prefName?: string;
  cityName?: string;
  address?: string;
  chiban?: string;
  realEstate: string;
}): string => {
  const { prefName, cityName, address, chiban, realEstate } = params;
  // 市区町村名がaddressの先頭にある場合のみ取り除く（replace()だと途中の一致も置換してしまうため）
  const locationWithoutCity =
    address && cityName && address.startsWith(cityName)
      ? address.slice(cityName.length)
      : address;
  if (!prefName || !cityName || !locationWithoutCity || !chiban) {
    return "/acquirebook";
  }

  const searchParams = new URLSearchParams({
    prefecture: prefName,
    city: cityName,
    location: NYHalfWidthNumberToFillWidthNumber(locationWithoutCity),
    chiban: NYHalfWidthNumberToFillWidthNumber(chiban),
    realEstate,
  });

  return `/acquirebook?${searchParams.toString()}`;
};

/**
 * 受付帳ポップアップの内容を表示するReactコンポーネント
 * 地図上で受付帳データをピン表示した際のポップアップに使用
 */
export const PopupReceptionBook: React.FC<IPopupBookContent> = (
  props: IPopupBookContent
) => {
  const {
    book,
    lat,
    lng,
    chibanRawData,
    crm = false,
    initialTags,
    initialRemarks,
    initialAdditionalFields,
    onTagsChange,
    onRemarksChange,
    onAdditionalFieldsChange,
    handlePinSave,
    receptionBookIndex,
    availableFields,
    hasAvailableFields,
    // 地番検索
    kaokuNumberSearchFlag = false,
    handleKaokuNumberSearch,
    // 役所資料取得依頼
    realEstateDocumentFetchFlag = false,
    handleRealEstateDocumentFetchModal,
  } = props;

  const map = useMap();

  // メモモーダル（タグ・備考・カスタム項目編集）の表示状態
  const [memoModalOpen, setMemoModalOpen] = useState(false);

  // その他操作モーダルの表示状態
  const [otherButtonModalOpen, setOtherButtonModalOpen] = useState(false);

  // タグ・備考・カスタム項目の現在値を管理（モーダルに渡すため）
  const [currentTags, setCurrentTags] = useState(initialTags ?? []);
  const [currentRemarks, setCurrentRemarks] = useState(initialRemarks ?? "");
  const [currentAdditionalFields, setCurrentAdditionalFields] = useState(
    initialAdditionalFields ?? ""
  );

  // 親コンポーネントから渡される初期値が変更された場合、内部状態を同期
  useEffect(() => {
    setCurrentTags(initialTags ?? []);
    setCurrentRemarks(initialRemarks ?? "");
    setCurrentAdditionalFields(initialAdditionalFields ?? "");
  }, [initialTags, initialRemarks, initialAdditionalFields]);

  // 受付帳ポップアップのタグ更新イベントリスナー（CRMモード時のみ）
  useEffect(() => {
    if (!crm || receptionBookIndex === undefined) return;

    const cleanup = addReceptionBookTagUpdateListener(
      receptionBookIndex,
      (newTags) => {
        setCurrentTags(newTags);
      }
    );

    return cleanup;
  }, [crm, receptionBookIndex]);

  // 受付帳ポップアップの備考更新イベントリスナー（CRMモード時のみ）
  useEffect(() => {
    if (!crm || receptionBookIndex === undefined) return;

    const cleanup = addReceptionBookRemarksUpdateListener(
      receptionBookIndex,
      (newRemarks) => {
        setCurrentRemarks(newRemarks);
      }
    );

    return cleanup;
  }, [crm, receptionBookIndex]);

  // 受付帳ポップアップの追加項目更新イベントリスナー（CRMモード時のみ）
  useEffect(() => {
    if (!crm || receptionBookIndex === undefined) return;

    const cleanup = addReceptionBookAdditionalFieldsUpdateListener(
      receptionBookIndex,
      (newAdditionalFields) => {
        setCurrentAdditionalFields(newAdditionalFields);
      }
    );

    return cleanup;
  }, [crm, receptionBookIndex]);

  // Google Maps（衛星写真）のURL
  const googleMapsUrl = getGoogleMapsUrl(map, { lat, lng });

  const { priceMap } = useFeatureFlags();

  // 地価マップのURL
  const chikaMapUrl = getChikaMapUrl({ lat, lng });

  // 地番・家屋番号検索ボタンの有効/無効判定（都道府県名・所在・地番が全て値がある場合のみ有効）
  const kaokuNumberSearchButtonState: boolean =
    (chibanRawData?.prefName ?? "") !== "" &&
    (book?.address ?? "") !== "" &&
    (book?.chiban ?? "") !== "";

  // 地番・家屋番号検索ハンドラー
  // 都道府県名はchibanRawData（地図データ）から取得する（bookには含まれていないため）
  const handleClickKaokuNumberSearchButton =
    handleKaokuNumberSearch &&
    kaokuNumberSearchFlag &&
    book &&
    kaokuNumberSearchButtonState
      ? () => {
          // 受付帳から地番情報を抽出
          const chibanInput: ChibanInput = {
            prefectureName: chibanRawData?.prefName ?? "",
            location: book.address ?? "",
            chiban: book.chiban ?? "",
            bookType: "LAND", // 受付帳はデフォルトで土地として扱う
            latitude: lat,
            longitude: lng,
          };
          handleKaokuNumberSearch([chibanInput]);
        }
      : undefined;

  // 役所資料取得依頼ハンドラー
  // 都道府県名はchibanRawData（地図データ）から取得する（bookには含まれていないため）
  const handleRealEstateDocumentFetch =
    handleRealEstateDocumentFetchModal && realEstateDocumentFetchFlag && book
      ? () => {
          handleRealEstateDocumentFetchModal(
            chibanRawData?.prefName ?? "",
            "", // 住居表示は受付帳にないので空
            book.chiban ?? "",
            book.address ?? "",
            true
          );
        }
      : undefined;

  if (book) {
    const chiban = book.chiban === "" ? "地番記録なし" : book.chiban;

    // ポップアップ幅の計算
    const mapContainerSizeX = map.getSize().x;
    let popupWidth = 320;
    if (mapContainerSizeX < 375) {
      popupWidth = Math.floor(mapContainerSizeX * 0.8);
    }

    return (
      <Box sx={{ width: popupWidth }}>
        <FormControl>
          <FormLabel sx={{ fontSize: "0.75rem", mb: 0.5 }}>
            <DescriptionIcon fontSize="inherit" sx={{ mr: 0.3 }} />
            登記原因
          </FormLabel>
          <Typography
            ml={2}
            my={0.5}
            fontSize={"0.7rem"}
            variant={"body1"}
            component={"span"}
          >
            {book.receptionReason ?? "未設定"}
          </Typography>
          <FormLabel sx={{ fontSize: "0.75rem", mb: 0.5 }}>
            <EventIcon fontSize="inherit" sx={{ mr: 0.3 }} />
            受付年月日
          </FormLabel>
          <Typography
            ml={2}
            my={0.5}
            fontSize={"0.7rem"}
            variant={"body1"}
            component={"span"}
          >
            {formatDate(book.legalAffairsBureauRequestDate)}
          </Typography>
          <FormLabel sx={{ fontSize: "0.75rem", mb: 0.5 }}>
            <PlaceIcon fontSize="inherit" sx={{ mr: 0.3 }} />
            所在
          </FormLabel>
          <Typography
            ml={2}
            my={0.5}
            fontSize={"0.7rem"}
            variant={"body1"}
            component={"span"}
          >
            {NYHalfWidthNumberToFillWidthNumber(book.address ?? "未設定")}
          </Typography>
          <FormLabel sx={{ fontSize: "0.75rem", mb: 0.5 }}>
            <ImportContactsIcon fontSize="inherit" sx={{ mr: 0.3 }} />
            地番
          </FormLabel>
          <Typography
            ml={2}
            my={0.5}
            fontSize={"0.7rem"}
            variant={"body1"}
            component={"span"}
          >
            {NYHalfWidthNumberToFillWidthNumber(chiban)}
          </Typography>
          <FormLabel sx={{ fontSize: "0.75rem", mb: 0.5 }}>
            <PlaceIcon fontSize="inherit" sx={{ mr: 0.3 }} />
            緯度経度
          </FormLabel>
          <Typography
            ml={2}
            my={0.5}
            fontSize={"0.7rem"}
            variant={"body1"}
            component={"span"}
          >
            {lat.toFixed(6)}, {lng.toFixed(6)}
          </Typography>
          <FormLabel sx={{ fontSize: "0.75rem", mb: 0.5 }}>
            <SquareFootIcon fontSize="inherit" sx={{ mr: 0.3 }} />
            推計地積(㎡)
          </FormLabel>
          <Typography
            ml={2}
            my={0.5}
            fontSize={"0.7rem"}
            variant={"body1"}
            component={"span"}
          >
            {formatArea(chibanRawData?.area ?? null)}
          </Typography>
        </FormControl>
        {crm && (
          <Box mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Button
                  label={"登記を取得"}
                  disabled={
                    !chibanRawData?.prefName ||
                    !chibanRawData?.cityName ||
                    !book.address ||
                    !book.chiban
                  }
                  onClick={() => {
                    const url = buildAcquireBookUrl({
                      prefName: chibanRawData?.prefName,
                      cityName: chibanRawData?.cityName,
                      address: book.address,
                      chiban: book.chiban,
                      realEstate: "LAND",
                    });
                    window.open(url, "_blank");
                  }}
                  size={SizingWrapperStyle.INHERIT}
                  variant={ButtonVariantOption.Contained}
                  color={ButtonColorOption.Primary}
                />
              </Grid>
              <Grid item xs={6}>
                <Button
                  label={"その他操作"}
                  onClick={() => {
                    setOtherButtonModalOpen(true);
                  }}
                  size={SizingWrapperStyle.INHERIT}
                  variant={ButtonVariantOption.Outlined}
                  color={ButtonColorOption.Primary}
                />
              </Grid>
              <Grid item xs={6}>
                <Button
                  label={"メモ"}
                  onClick={() => {
                    setMemoModalOpen(true);
                  }}
                  size={SizingWrapperStyle.INHERIT}
                  variant={ButtonVariantOption.Outlined}
                  color={ButtonColorOption.Primary}
                  startIcon={<NoteAltIcon />}
                />
              </Grid>
            </Grid>
          </Box>
        )}
        <MemoModal
          open={memoModalOpen}
          onClose={() => {
            setMemoModalOpen(false);
          }}
          initialTags={currentTags}
          initialRemarks={currentRemarks}
          initialAdditionalFields={currentAdditionalFields}
          onTagsChange={(newTags) => {
            setCurrentTags(newTags);
            if (onTagsChange) {
              onTagsChange(newTags);
            }
          }}
          onRemarksChange={(newRemarks) => {
            setCurrentRemarks(newRemarks);
            if (onRemarksChange) {
              onRemarksChange(newRemarks);
            }
          }}
          onAdditionalFieldsChange={(newAdditionalFields) => {
            setCurrentAdditionalFields(newAdditionalFields);
            // 同期イベントを発火（他のポップアップや将来の再表示に備える）
            if (receptionBookIndex !== undefined) {
              dispatchReceptionBookAdditionalFieldsUpdate(
                receptionBookIndex,
                newAdditionalFields
              );
            }
            // 親コンポーネントへの通知
            if (onAdditionalFieldsChange) {
              onAdditionalFieldsChange(newAdditionalFields);
            }
          }}
          availableFields={availableFields}
          hasAvailableFields={hasAvailableFields}
        />
        <OtherButtonModal
          open={otherButtonModalOpen}
          onClose={() => {
            setOtherButtonModalOpen(false);
          }}
          kaokuNumberSearchFlag={kaokuNumberSearchFlag}
          handleKaokuNumberSearch={handleClickKaokuNumberSearchButton}
          handlePinSave={handlePinSave}
          realEstateDocumentFetchFlag={realEstateDocumentFetchFlag}
          handleRealEstateDocumentFetchModal={handleRealEstateDocumentFetch}
          // 受付帳には住居表示データがなく、物件概要書作成APIは住居表示を必須としているため無効化
          propertySummary={false}
          handleNewPropertySheet={undefined}
          googleMapsUrl={googleMapsUrl}
          chikaMapUrl={priceMap ? chikaMapUrl : undefined}
        />
      </Box>
    );
  }
  return <>受付帳情報が取得できませんでした</>;
};
